import { Link, useLocation } from 'react-router-dom';
import { useState, useEffect } from 'react';

export default function Navbar() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isServicesOpen, setIsServicesOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isDesktopServicesOpen, setIsDesktopServicesOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setIsMenuOpen(false);
    setIsServicesOpen(false);
    setIsDesktopServicesOpen(false);
  }, [location]);

  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMenuOpen]);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const services = [
    { name: 'Paving Estimate', path: '/services/paving-estimate' },
    { name: 'Asphalt Repair', path: '/services/asphalt-repair' },
    { name: 'Concrete Driveway', path: '/services/concrete-driveway' },
    { name: 'Sealcoating', path: '/services/sealcoating' },
    { name: 'Pothole Repair', path: '/services/pothole-repair' },
    { name: 'Parking Lot', path: '/services/parking-lot' },
    { name: 'Commercial Asphalt', path: '/services/commercial-asphalt' },
    { name: 'Maintenance Services', path: '/services/maintenance-services' }
  ];

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-zinc-900 shadow-lg' : 'bg-zinc-900'}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-3 cursor-pointer">
            <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-orange-600 rounded-lg flex items-center justify-center">
              <i className="ri-road-map-line text-white text-xl"></i>
            </div>
            <span className="text-xl font-bold text-zinc-100" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              LINCOLN PAVING
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-1">
            <Link to="/" className="text-zinc-100 hover:bg-zinc-700 px-4 py-2 rounded-lg font-medium transition-colors whitespace-nowrap cursor-pointer">
              Home
            </Link>
            <Link to="/about" className="text-zinc-100 hover:bg-zinc-700 px-4 py-2 rounded-lg font-medium transition-colors whitespace-nowrap cursor-pointer">
              About
            </Link>
            
            {/* Desktop Services Dropdown */}
            <div className="relative">
              <button 
                onClick={() => setIsDesktopServicesOpen(!isDesktopServicesOpen)}
                onMouseEnter={() => setIsDesktopServicesOpen(true)}
                className="text-zinc-100 hover:bg-zinc-700 px-4 py-2 rounded-lg font-medium transition-colors whitespace-nowrap cursor-pointer flex items-center"
              >
                Services
                <i className={`ri-arrow-down-s-line ml-1 transition-transform ${isDesktopServicesOpen ? 'rotate-180' : ''}`}></i>
              </button>
              
              {isDesktopServicesOpen && (
                <div 
                  className="absolute top-full left-0 mt-2 w-64 bg-white rounded-lg shadow-xl py-2 z-50"
                  onMouseLeave={() => setIsDesktopServicesOpen(false)}
                >
                  {services.map((service, index) => (
                    <Link
                      key={index}
                      to={service.path}
                      className="block px-4 py-2.5 text-zinc-700 hover:bg-orange-50 hover:text-orange-600 transition-colors cursor-pointer text-sm font-medium"
                    >
                      {service.name}
                    </Link>
                  ))}
                </div>
              )}
            </div>

            <Link to="/blog" className="text-zinc-100 hover:bg-zinc-700 px-4 py-2 rounded-lg font-medium transition-colors whitespace-nowrap cursor-pointer">
              Blog
            </Link>
            <Link to="/contact" className="text-zinc-100 hover:bg-zinc-700 px-4 py-2 rounded-lg font-medium transition-colors whitespace-nowrap cursor-pointer">
              Contact
            </Link>
          </div>

          {/* CTA Button & Mobile Menu */}
          <div className="flex items-center space-x-4">
            <a href="tel:3084705637" className="hidden sm:flex items-center space-x-2 bg-orange-600 text-white px-5 py-2.5 rounded-lg font-semibold hover:bg-orange-700 transition-colors whitespace-nowrap cursor-pointer">
              <i className="ri-phone-line text-lg"></i>
              <span>(308) 470-5637</span>
            </a>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="lg:hidden text-zinc-100 hover:bg-zinc-700 p-2 rounded-lg transition-colors cursor-pointer"
              aria-label="Toggle menu"
            >
              <i className={`${isMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`}></i>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      <div className={`lg:hidden fixed inset-0 top-20 bg-zinc-900 transform transition-transform duration-300 ease-in-out ${isMenuOpen ? 'translate-x-0' : 'translate-x-full'} overflow-y-auto`}>
        <div className="px-4 py-6 space-y-2">
          <Link to="/" className="block text-zinc-100 hover:bg-zinc-700 px-4 py-4 rounded-lg font-medium transition-colors cursor-pointer">
            Home
          </Link>
          <Link to="/about" className="block text-zinc-100 hover:bg-zinc-700 px-4 py-4 rounded-lg font-medium transition-colors cursor-pointer">
            About
          </Link>
          
          {/* Mobile Services Accordion */}
          <div>
            <button
              onClick={() => setIsServicesOpen(!isServicesOpen)}
              className="w-full flex items-center justify-between text-zinc-100 hover:bg-zinc-700 px-4 py-4 rounded-lg font-medium transition-colors cursor-pointer"
            >
              <span>Services</span>
              <i className={`ri-arrow-down-s-line text-xl transition-transform ${isServicesOpen ? 'rotate-180' : ''}`}></i>
            </button>
            
            {isServicesOpen && (
              <div className="mt-2 ml-4 space-y-1">
                {services.map((service, index) => (
                  <Link
                    key={index}
                    to={service.path}
                    className="block text-zinc-300 hover:text-orange-500 hover:bg-zinc-800 px-4 py-3 rounded-lg transition-colors cursor-pointer text-sm"
                  >
                    {service.name}
                  </Link>
                ))}
              </div>
            )}
          </div>

          <Link to="/blog" className="block text-zinc-100 hover:bg-zinc-700 px-4 py-4 rounded-lg font-medium transition-colors cursor-pointer">
            Blog
          </Link>
          <Link to="/contact" className="block text-zinc-100 hover:bg-zinc-700 px-4 py-4 rounded-lg font-medium transition-colors cursor-pointer">
            Contact
          </Link>

          <a href="tel:3084705637" className="flex items-center justify-center space-x-2 bg-orange-600 text-white px-5 py-4 rounded-lg font-semibold hover:bg-orange-700 transition-colors cursor-pointer mt-4">
            <i className="ri-phone-line text-lg"></i>
            <span>(308) 470-5637</span>
          </a>
        </div>
      </div>
    </nav>
  );
}